import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Crown, ShieldCheck, Sparkles, ArrowRight, Bot, Globe,
  MessageSquare, Workflow, CreditCard, Calendar as CalendarIcon,
  FileText, Zap, CheckCircle2, Lock, Rocket, Layers, DollarSign,
  Send, UserCheck, PhoneCall, Inbox,
} from 'lucide-react';
import './AutopilotFunnel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/Formalizeai/4W24x';

const STEPS = ['Apply', 'Tour', 'Deploy'];

/* -------------------------------------------------------------------- */
/* Step Indicator                                                        */
/* -------------------------------------------------------------------- */

const StepIndicator = ({ current }) => (
  <div className="ap-steps" data-testid="ap-step-indicator">
    {STEPS.map((label, i) => {
      const idx = i + 1;
      const state =
        idx < current ? 'ap-step-done' : idx === current ? 'ap-step-active' : '';
      return (
        <React.Fragment key={label}>
          <div className={`ap-step ${state}`}>
            <span className="ap-step-num">{idx < current ? '✓' : idx}</span>
            {label}
          </div>
          {idx < STEPS.length && <span className="ap-step-divider" />}
        </React.Fragment>
      );
    })}
  </div>
);

/* -------------------------------------------------------------------- */
/* Step 1 — Application (lead capture)                                   */
/* -------------------------------------------------------------------- */

const ApplicationStep = ({ onCaptured }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessNiche: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.email.trim()) {
      setError('Please fill in your first name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/autopilot/lead`, {
        ...form,
        source: 'autopilot-funnel-step-1',
      });
      onCaptured({ ...form, leadId: data?.leadId });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(', ')
          : detail || 'We could not submit your application. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ap-section ap-hero" data-testid="ap-step-1">
      <div className="ap-eyebrow">
        <Crown size={12} /> Private Briefing · By Application
      </div>
      <h1 className="ap-display" style={{ marginTop: 28 }}>
        We Build The Business.<br />
        <span className="ap-shimmer">The AI Runs It.</span>
      </h1>
      <p>
        Whether you already have a business or you&apos;re starting from zero, The Collins Academy
        builds and deploys a complete AI infrastructure — your website, your sales agent,
        your booking, your billing, your support — all running 24/7 without you. Apply below
        to take the tour.
      </p>

      <form className="ap-form" onSubmit={submit} data-testid="ap-application-form">
        <div className="ap-field">
          <label htmlFor="ap-fn">First Name</label>
          <input
            id="ap-fn"
            type="text"
            value={form.firstName}
            onChange={update('firstName')}
            placeholder="Marcus"
            data-testid="ap-input-firstName"
            required
          />
        </div>
        <div className="ap-field">
          <label htmlFor="ap-ln">Last Name</label>
          <input
            id="ap-ln"
            type="text"
            value={form.lastName}
            onChange={update('lastName')}
            placeholder="Collins"
            data-testid="ap-input-lastName"
          />
        </div>
        <div className="ap-field">
          <label htmlFor="ap-em">Email Address</label>
          <input
            id="ap-em"
            type="email"
            value={form.email}
            onChange={update('email')}
            placeholder="you@yourdomain.com"
            data-testid="ap-input-email"
            required
          />
        </div>
        <div className="ap-field">
          <label htmlFor="ap-ph">Mobile Number</label>
          <input
            id="ap-ph"
            type="tel"
            value={form.phone}
            onChange={update('phone')}
            placeholder="(555) 123-4567"
            data-testid="ap-input-phone"
          />
        </div>
        <div className="ap-field">
          <label htmlFor="ap-niche">What does your business do? <span style={{ opacity: 0.5 }}>(or what would you like it to do?)</span></label>
          <input
            id="ap-niche"
            type="text"
            value={form.businessNiche}
            onChange={update('businessNiche')}
            placeholder="Coach, agency, real estate, e-com, brand new idea…"
            data-testid="ap-input-niche"
          />
        </div>

        {error && (
          <div className="ap-form-error" data-testid="ap-form-error">{error}</div>
        )}

        <button
          type="submit"
          className="ap-btn ap-btn-primary ap-btn-block"
          disabled={submitting}
          data-testid="ap-application-submit"
        >
          {submitting ? 'Unlocking The Tour…' : (<>See My AI Infrastructure <ArrowRight size={18} /></>)}
        </button>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="ap-trust">
            <ShieldCheck size={14} /> Your information is encrypted &amp; never shared
          </div>
        </div>
      </form>
    </section>
  );
};

/* -------------------------------------------------------------------- */
/* Step 2 — Tour (interactive briefing)                                  */
/* -------------------------------------------------------------------- */

const PROMISES = [
  { icon: Globe, title: 'Niche-Specific AI Website', desc: 'Custom domain, premium design, on-brand copy — built for your industry from scratch.' },
  { icon: Bot, title: '24/7 AI Sales Closer', desc: 'A trained AI agent that qualifies leads, answers objections, books calls, and closes deals — even at 3 AM.' },
  { icon: MessageSquare, title: 'AI Customer Support', desc: 'Handles FAQs, order status, refunds, and policy questions in seconds. Slashes manual support to near-zero.' },
  { icon: Workflow, title: 'CRM + Pipeline', desc: 'Every lead and conversation flows into a clean pipeline automatically. No data entry. No leaks.' },
  { icon: CreditCard, title: 'Payment Processing', desc: 'Stripe / Square / Fanbasis wired into your funnel so clients pay directly from the chat.' },
  { icon: CalendarIcon, title: 'Auto-Booking', desc: 'Calendar fills with pre-qualified discovery calls. The AI does the heavy lifting; you just show up.' },
  { icon: FileText, title: 'Contracts & Onboarding', desc: 'E-signed contracts and onboarding sequences fire the second a client pays.' },
  { icon: Zap, title: 'End-to-End Workflow', desc: 'Lead → qualified → closed → onboarded → invoiced → reviewed. Fully hands-free.' },
];

const FLOW_STAGES = [
  { icon: Send, title: 'Lead Arrives', detail: 'Visitor lands on your AI website from social, search, or ads.' },
  { icon: Bot, title: 'AI Qualifies', detail: 'Sales agent opens a conversation, scores intent, handles objections.' },
  { icon: CalendarIcon, title: 'Auto-Booked', detail: 'Hot leads drop straight into your calendar — no human touch.' },
  { icon: CreditCard, title: 'Payment Collected', detail: 'Checkout runs inside the chat. Receipt + tax handled automatically.' },
  { icon: UserCheck, title: 'Client Onboarded', detail: 'Contracts signed, welcome email sent, kickoff steps triggered.' },
  { icon: Inbox, title: 'Profit Lands', detail: 'You wake up to booked calls, signed clients, and money in the account.' },
];

const FlowVisualizer = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % FLOW_STAGES.length), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="ap-flow" data-testid="ap-flow-visualizer">
      <div className="ap-flow-track">
        {FLOW_STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const state = i === active ? 'ap-flow-active' : i < active ? 'ap-flow-done' : '';
          return (
            <div key={stage.title} className={`ap-flow-node ${state}`}>
              <div className="ap-flow-icon"><Icon size={20} /></div>
              <div className="ap-flow-label">{stage.title}</div>
              {i < FLOW_STAGES.length - 1 && <div className="ap-flow-connector" />}
            </div>
          );
        })}
      </div>
      <div className="ap-flow-detail" key={active}>
        <span className="ap-flow-detail-num">{String(active + 1).padStart(2, '0')}</span>
        <div>
          <h4>{FLOW_STAGES[active].title}</h4>
          <p>{FLOW_STAGES[active].detail}</p>
        </div>
      </div>
    </div>
  );
};

const CHAT_SCRIPT = [
  { who: 'lead', text: 'Hey, do you guys offer monthly retainers?' },
  { who: 'ai',   text: "Hey Jordan — yes, we do. Quick question: are you running ads already or starting from scratch?" },
  { who: 'lead', text: 'Running ads but conversions are weak.' },
  { who: 'ai',   text: "Got it. Sounds like a funnel & follow-up issue, not an ad issue. I have a 15-min slot tomorrow at 2:10 PM EST — want me to lock it in?" },
  { who: 'lead', text: 'Yeah that works.' },
  { who: 'ai',   text: '✅ Booked. Calendar invite + Zoom link just hit your inbox. Talk soon.' },
];

const ChatDemo = () => {
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    if (visible >= CHAT_SCRIPT.length) {
      const reset = setTimeout(() => setVisible(1), 4200);
      return () => clearTimeout(reset);
    }
    const id = setTimeout(() => setVisible((v) => v + 1), 1600);
    return () => clearTimeout(id);
  }, [visible]);

  return (
    <div className="ap-chat" data-testid="ap-chat-demo">
      <div className="ap-chat-head">
        <span className="ap-live-dot" /> AI Sales Agent · Live
      </div>
      <div className="ap-chat-body">
        {CHAT_SCRIPT.slice(0, visible).map((msg, i) => (
          <div key={i} className={`ap-bubble ap-bubble-${msg.who}`}>
            {msg.text}
          </div>
        ))}
        {visible < CHAT_SCRIPT.length && (
          <div className={`ap-bubble ap-bubble-${CHAT_SCRIPT[visible]?.who} ap-typing`}>
            <span /><span /><span />
          </div>
        )}
      </div>
    </div>
  );
};

const BriefingStep = ({ lead, onNext }) => (
  <section className="ap-section" data-testid="ap-step-2">
    <div style={{ textAlign: 'center' }}>
      <div className="ap-eyebrow"><Sparkles size={12} /> Your Tour, {lead?.firstName || 'Entrepreneur'}</div>
      <h2 className="ap-display" style={{ marginTop: 26, fontSize: 'clamp(34px, 5vw, 56px)' }}>
        Meet Your <span className="ap-shimmer">Autonomous Business</span>
      </h2>
      <p style={{ maxWidth: 720, margin: '24px auto 0', color: 'var(--ap-ink-dim)', fontSize: 17, lineHeight: 1.6 }}>
        Most founders are the bottleneck in their own business. The Complete AI Infrastructure
        replaces the manual chaos — sales, support, follow-up, onboarding, invoicing — with a
        single autonomous digital employee built around your offer. Here&apos;s what we deploy.
      </p>
    </div>

    <FlowVisualizer />

    <div className="ap-split">
      <div>
        <h3 className="ap-h3"><Bot size={18} /> Watch The AI Sales Agent Close A Lead</h3>
        <p className="ap-mute">
          This is a live simulation of how your AI closer handles inbound traffic — qualifying,
          objection-handling, and booking the call. Hands-free.
        </p>
      </div>
      <ChatDemo />
    </div>

    <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 28 }}>
      <h3 className="ap-h3" style={{ justifyContent: 'center' }}><Layers size={18} /> Everything That Ships With Your Infrastructure</h3>
    </div>

    <div className="ap-grid">
      {PROMISES.map((p) => {
        const Icon = p.icon;
        return (
          <div key={p.title} className="ap-card">
            <div className="ap-card-icon"><Icon size={20} /></div>
            <h3>{p.title}</h3>
            <p>{p.desc}</p>
          </div>
        );
      })}
    </div>

    <div style={{ textAlign: 'center', marginTop: 40 }}>
      <button
        type="button"
        className="ap-btn ap-btn-primary"
        onClick={onNext}
        data-testid="ap-briefing-next"
      >
        Show Me My Package <ArrowRight size={18} />
      </button>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Step 3 — Deploy (single package, single CTA)                          */
/* -------------------------------------------------------------------- */

const INCLUDES = [
  'Custom AI Website (niche-specific design, copy & domain)',
  '24/7 AI Sales Agent (trained closer bot)',
  'AI Customer Support Agent',
  'CRM + Pipeline Integration',
  'Payment Processing Setup (Stripe / Fanbasis)',
  'Scheduling & Auto-Booking System',
  'Contracts & E-Sign Automation',
  'Client Onboarding Sequences',
  'Invoicing & Recurring Billing',
  'Full End-to-End Workflow Automation',
  'White-Glove Build & Deployment',
  'Live Training & Handoff Session',
];

const DeployStep = ({ lead }) => {
  const firstName = lead?.firstName || 'Entrepreneur';
  return (
    <section className="ap-section" data-testid="ap-step-3">
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div className="ap-eyebrow"><Rocket size={12} /> Deploy Your Infrastructure</div>
        <h2 className="ap-display" style={{ marginTop: 26, fontSize: 'clamp(34px, 5vw, 56px)' }}>
          One Package. <span className="ap-shimmer">Everything Included.</span>
        </h2>
        <p style={{ maxWidth: 680, margin: '20px auto 0', color: 'var(--ap-ink-dim)', fontSize: 16 }}>
          {firstName}, this is the full build — engineered, deployed, and handed to you by
          The Collins Academy team. No upsells. No tiers. No add-ons. One decision.
        </p>
      </div>

      <div className="ap-package" data-testid="ap-package-card">
        <div className="ap-package-head">
          <h3 className="ap-package-name">Complete AI Infrastructure</h3>
          <span className="ap-package-tier">Done-For-You</span>
        </div>
        <ul className="ap-package-list">
          {INCLUDES.map((line) => (
            <li key={line}><CheckCircle2 size={16} /> {line}</li>
          ))}
        </ul>
        <div className="ap-package-foot">
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ap-btn ap-btn-primary ap-btn-block"
            data-testid="ap-deploy-cta"
          >
            Deploy My Infrastructure <ArrowRight size={18} />
          </a>
          <div className="ap-trust">
            <Lock size={14} /> Secure checkout · Build queue is intentionally limited
          </div>
          <p className="ap-mute" style={{ textAlign: 'center', marginTop: 18 }}>
            Want to talk to a strategist first?{' '}
            <a
              href="https://calendly.com/thecollinsacademynew/30min"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a private call →
            </a>
          </p>
        </div>
      </div>

      <div className="ap-final-strip" data-testid="ap-final-strip">
        <div>
          <DollarSign size={18} />
          <span>Built to scale toward $100K months — without you.</span>
        </div>
        <div>
          <PhoneCall size={18} />
          <span>White-glove build with live handoff training.</span>
        </div>
        <div>
          <ShieldCheck size={18} />
          <span>Trusted by Collins Academy entrepreneurs nationwide.</span>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- */
/* Funnel orchestrator                                                   */
/* -------------------------------------------------------------------- */

const AutopilotFunnelPage = () => {
  const [step, setStep] = useState(1);
  const [lead, setLead] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const content = useMemo(() => {
    switch (step) {
      case 1:
        return (
          <ApplicationStep
            onCaptured={(payload) => {
              setLead(payload);
              setStep(2);
            }}
          />
        );
      case 2:
        return <BriefingStep lead={lead} onNext={() => setStep(3)} />;
      case 3:
      default:
        return <DeployStep lead={lead} />;
    }
  }, [step, lead]);

  return (
    <div className="ap-funnel" data-testid="autopilot-funnel">
      <StepIndicator current={step} />
      {content}
    </div>
  );
};

export default AutopilotFunnelPage;
